import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Clock, Receipt as ReceiptIcon,
  Wallet, Flame, UserCheck, MessageSquareWarning, Undo2, X,
} from "lucide-react";
import api, { fileUrl } from "../lib/api";
import DriverHint from "../components/DriverHint";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

/**
 * Vendor Issue Inbox · Section 1 of 6.0 roadmap.
 *
 * Three-column kanban: Open / In Progress / Resolved. Each card shows:
 *   • reporter + reason + severity + unread dot
 *   • issue message snippet
 *   • full line item + customization snapshot
 *   • opened_at age + payment / refund state
 * Clicking a card opens a right-hand sheet with full history (order events +
 * chat), action buttons (Take / Resolve / Refund), and the Phase 5 chat
 * thread so the vendor can respond without leaving the inbox.
 */

const COLUMNS = [
  { key: "open", label: "Open", color: "from-pink-500 to-rose-500", icon: Flame },
  { key: "in_progress", label: "In Progress", color: "from-amber-400 to-orange-500", icon: Clock },
  { key: "resolved", label: "Resolved", color: "from-emerald-400 to-teal-500", icon: CheckCircle2 },
];

function fmtCents(c) { return `$${(Number(c || 0) / 100).toFixed(2)}`; }
function fmtAge(ts) {
  if (!ts) return "";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function VendorIssueInbox() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [inbox, setInbox] = useState({ issues: [], counts: {}, unread_count: 0 });
  const [selectedId, setSelectedId] = useState(searchParams.get("open") || null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor/issues");
      setInbox(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);

  const grouped = {
    open: inbox.issues.filter((i) => i.state === "open"),
    in_progress: inbox.issues.filter((i) => i.state === "in_progress"),
    resolved: inbox.issues.filter((i) => i.state === "resolved"),
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="sticky top-0 z-30 backdrop-blur bg-black/80 border-b border-white/10 px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate("/shop/order-center")}
          className="w-10 h-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10"
          data-testid="issues-back-btn"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-black tracking-tight">Issue Inbox</h1>
          <p className="text-xs text-zinc-500">
            {loading ? "Loading…" : `${inbox.issues.length} ${inbox.issues.length === 1 ? "issue" : "issues"} · ${inbox.unread_count} unread`}
          </p>
        </div>
        {inbox.unread_count > 0 && (
          <span className="px-3 py-1.5 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-200 text-xs font-bold" data-testid="issues-unread-badge">
            {inbox.unread_count} new
          </span>
        )}
      </div>

      <DriverHint show={!loading && inbox.issues.length === 0} />

      <div className="px-6 py-6 overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-6">
          {COLUMNS.map((col) => {
            const items = grouped[col.key] || [];
            const Icon = col.icon;
            return (
              <div key={col.key} className="w-96 shrink-0 flex flex-col gap-3" data-testid={`issues-col-${col.key}`}>
                <div className="rounded-xl border border-white/10 bg-zinc-900 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${col.color} grid place-items-center`}>
                      <Icon className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h3 className="font-bold">{col.label}</h3>
                    <span className="ml-auto text-xs text-zinc-400">{items.length}</span>
                  </div>
                </div>
                {items.length === 0 ? (
                  <p className="text-center text-xs text-zinc-600 py-6">—</p>
                ) : items.map((i) => (
                  <button
                    key={i.order_id}
                    onClick={() => setSelectedId(i.order_id)}
                    className={`text-left rounded-2xl border p-4 transition-colors ${
                      i.unread_for_vendor
                        ? "bg-pink-500/5 border-pink-500/30 hover:border-pink-500/60"
                        : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]"
                    }`}
                    data-testid={`issue-card-${i.order_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-xs font-bold text-zinc-200">#{i.short_code}</span>
                      <div className="flex items-center gap-1.5">
                        {i.unread_for_vendor && <span className="w-2 h-2 rounded-full bg-pink-400" />}
                        <span className="text-[10px] text-zinc-500">{fmtAge(i.opened_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2 text-xs">
                      <span className={`px-2 py-0.5 rounded-full border ${
                        i.severity === "high"
                          ? "bg-red-500/15 border-red-500/40 text-red-200"
                          : "bg-amber-500/15 border-amber-500/40 text-amber-200"
                      }`}>
                        {(i.reason_code || "other").replace("_", " ")}
                      </span>
                      <span className="text-zinc-400 truncate">{i.reporter_name}</span>
                    </div>
                    {i.line_name && (
                      <div className="text-sm text-zinc-300 mb-1 truncate">
                        <span className="text-zinc-500">Item:</span> {i.line_name}
                      </div>
                    )}
                    <p className="text-xs text-zinc-400 line-clamp-2">{i.details}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5 text-[10px]">
                      <span className="text-zinc-500 uppercase">{i.payment_method} · {i.fulfillment_type}</span>
                      <span className="font-bold text-white">{fmtCents(i.total_cents)}</span>
                    </div>
                    {i.refunded_cents > 0 && (
                      <div className="text-[10px] text-amber-300 mt-1">Refunded {fmtCents(i.refunded_cents)}</div>
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <IssueDetailSheet
        orderId={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}

function IssueDetailSheet({ orderId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      const { data } = await api.get(`/vendor/issues/${orderId}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load");
      onClose();
    }
  }, [orderId, onClose]);

  useEffect(() => { if (orderId) load(); else setData(null); }, [orderId, load]);

  const act = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/vendor/issues/${orderId}${path}`, body || {});
      await load();
      onChanged?.();
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!orderId) return null;
  const i = data?.issue;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <aside
        className="fixed right-0 top-0 bottom-0 z-50 w-full sm:max-w-xl bg-zinc-950 border-l border-white/10 overflow-y-auto"
        data-testid="issue-detail-sheet"
      >
        <div className="sticky top-0 bg-zinc-950/95 backdrop-blur px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black">#{i?.short_code || "…"}</h2>
            <p className="text-xs text-zinc-500">{i?.buyer_name} · {i?.reason_code?.replace("_", " ")}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/10" data-testid="issue-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!data ? (
          <div className="p-6 text-sm text-zinc-500">Loading…</div>
        ) : (
          <div className="p-6 space-y-5">
            {/* State chip */}
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                i.state === "open" ? "bg-pink-500/15 border border-pink-500/40 text-pink-200" :
                i.state === "in_progress" ? "bg-amber-500/15 border border-amber-500/40 text-amber-200" :
                "bg-emerald-500/15 border border-emerald-500/40 text-emerald-200"
              }`}>
                {i.state === "open" && <Flame className="w-3 h-3" />}
                {i.state === "in_progress" && <Clock className="w-3 h-3" />}
                {i.state === "resolved" && <CheckCircle2 className="w-3 h-3" />}
                {i.state.replace("_", " ")}
              </span>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Max refundable</div>
                <div className="font-bold">{fmtCents(i.max_refundable_cents)}</div>
              </div>
            </div>

            {/* Customer details */}
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-4">
              <div className="text-[10px] uppercase tracking-wider text-red-300 font-bold mb-1">{i.reason_code?.replace("_", " ")}</div>
              <p className="text-sm text-red-100">{i.details}</p>
              {i.line_name && <p className="text-xs text-zinc-300 mt-2">Flagged item: <span className="font-semibold">{i.line_name}</span></p>}
            </div>

            {/* Order items (full customization) */}
            <div>
              <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Order items</h3>
              <div className="space-y-2">
                {i.items.map((it) => (
                  <div key={it.line_id || it.product_id} className={`rounded-xl border p-3 ${it.line_id === i.line_id ? "border-red-500/40 bg-red-500/5" : "border-white/10 bg-white/[0.03]"}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">× {it.quantity} {it.name}</div>
                      <div className="text-sm font-bold">{fmtCents(it.line_total_cents)}</div>
                    </div>
                    {it.customization_summary?.variation && <div className="text-xs text-zinc-400 mt-0.5">Size: {it.customization_summary.variation}</div>}
                    {(it.customization_summary?.modifiers || []).length > 0 && (
                      <div className="text-xs text-zinc-400 mt-0.5">+ {it.customization_summary.modifiers.join(", ")}</div>
                    )}
                    {(it.customization_summary?.ingredients || []).length > 0 && (
                      <div className="text-xs text-amber-300 mt-0.5">{it.customization_summary.ingredients.join(" · ")}</div>
                    )}
                    {it.customization_summary?.special_instructions && (
                      <div className="text-xs italic text-pink-300 mt-1">"{it.customization_summary.special_instructions}"</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Chat */}
            {data.messages?.length > 0 && (
              <div>
                <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Conversation</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {data.messages.slice(-20).map((m) => (
                    <div key={m.message_id} className={`text-xs rounded-lg px-3 py-2 ${m.is_vendor ? "bg-pink-500/10 border border-pink-500/30" : "bg-white/5 border border-white/10"}`}>
                      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold mb-0.5">{m.sender_name}{m.sender_role === "buyer" ? "" : ` · ${m.sender_role}`}</div>
                      <div className="text-zinc-200">{m.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2">
              {i.state === "open" && (
                <Button disabled={busy} onClick={() => act("/take", { note: "" })} className="h-11 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold col-span-2" data-testid="issue-action-take">
                  <UserCheck className="w-4 h-4 mr-1" /> Take this issue
                </Button>
              )}
              {data.can_refund && i.max_refundable_cents > 0 && (
                <Button disabled={busy} onClick={() => setRefundOpen(true)} variant="outline" className="h-10 rounded-full border-white/10 text-white hover:bg-white/5" data-testid="issue-action-refund">
                  <Wallet className="w-4 h-4 mr-1" /> Refund
                </Button>
              )}
              {i.state !== "resolved" && (
                <Button disabled={busy} onClick={() => act("/resolve", { note: "Resolved" })} className="h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="issue-action-resolve">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                </Button>
              )}
            </div>

            {/* History */}
            {data.history?.length > 0 && (
              <div>
                <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Audit trail</h3>
                <div className="space-y-1.5">
                  {data.history.slice().reverse().map((ev, idx) => (
                    <div key={idx} className="text-[11px] text-zinc-400 flex items-start gap-2">
                      <ReceiptIcon className="w-3 h-3 mt-0.5 text-zinc-600" />
                      <div className="flex-1">
                        <div><span className="font-semibold text-zinc-200">{ev.action.replace("_", " ")}</span> · {ev.actor_name}{ev.actor_role ? ` (${ev.actor_role})` : ""}</div>
                        {ev.reason && <div className="text-zinc-500">{ev.reason}</div>}
                        <div className="text-zinc-600">{new Date(ev.ts).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </aside>

      <RefundDialog
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        orderId={orderId}
        maxCents={i?.max_refundable_cents || 0}
        onDone={() => { setRefundOpen(false); load(); onChanged?.(); }}
      />
    </>
  );
}

function RefundDialog({ open, onClose, orderId, maxCents, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState("full");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAmount(""); setReason(""); setKind("full"); } }, [open]);
  const amtCents = kind === "full" ? maxCents : Math.round(Number(amount || 0) * 100);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/vendor/orders/${orderId}/refund`, { reason: reason.trim(), amount_cents: amtCents });
      toast.success("Refund processed");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white" data-testid="issue-refund-dialog">
        <DialogHeader>
          <DialogTitle>Refund from issue</DialogTitle>
          <DialogDescription className="text-zinc-500">Max refundable: {fmtCents(maxCents)}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <button onClick={() => setKind("full")} className={`flex-1 h-10 rounded-full text-sm font-semibold border ${kind === "full" ? "bg-pink-500/20 border-pink-500/60" : "bg-white/5 border-white/10"}`} data-testid="issue-refund-full">Full</button>
          <button onClick={() => setKind("partial")} className={`flex-1 h-10 rounded-full text-sm font-semibold border ${kind === "partial" ? "bg-pink-500/20 border-pink-500/60" : "bg-white/5 border-white/10"}`} data-testid="issue-refund-partial">Partial</button>
        </div>
        {kind === "partial" && (
          <input type="number" step="0.01" min={0.01} max={maxCents / 100} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount in dollars" data-testid="issue-refund-amount" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm" />
        )}
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="issue-refund-reason" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm resize-none" />
        <div className="flex gap-2 justify-end">
          <Button onClick={onClose} variant="outline" className="rounded-full border-white/10 text-zinc-300">Back</Button>
          <Button disabled={busy || reason.trim().length < 3 || amtCents < 1 || amtCents > maxCents} onClick={submit} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white" data-testid="issue-refund-confirm">
            Refund {fmtCents(amtCents)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
